/**
 * ARP — scales, chords, buildSeq and the SyrinxLite voice.
 *
 * Chirp provenance: Chirp/trill.html (SCALES, chordTones, triadQuality,
 * buildSeq, SYRINX). The sequence builder is pure and tested; the scene only
 * schedules it on the shared transport and lets gestures ride the filter.
 */

import { createArpEngine, type ArpEngine, ARP_VOICES, ARP_VOICE_IDS, type ArpVoiceId } from '../audio/arp/syrinx-lite';
import type { Task } from '../audio/transport';
import { stepSeconds } from '../audio/transport';
import { ArpAdapter } from '../mapping/adapters';
import type { ArpIntent, ControlState, FeatureFrame } from '../mapping/types';
import {
  ARP_PATTERNS, buildSeq, QUALITIES, SCALES, SCALE_IDS,
  triadQuality, type ArpPattern, type Quality, type ScaleId,
} from '../music/scales';
import { chips, el, panel, slider, toggle } from '../ui/controls';
import type { Scene, SceneContext } from './types';

export function createArpScene(): Scene {
  let engine: ArpEngine | null = null;
  let ctxRef: SceneContext | null = null;
  let task: Task | null = null;
  let scale: ScaleId = 'minor';
  let quality: Quality = 'seventh';
  let pattern: ArpPattern = 'up';
  let octaves = 2;
  let degree = 0;
  let bpm = 122;
  let division = 4;
  let base = 57; // A3
  let seq: number[] = [];
  let idx = 0;
  const adapter = new ArpAdapter();
  let last: ArpIntent | null = null;
  let chordLabel = '';

  function rebuild(): void {
    seq = buildSeq(scale, degree, quality, octaves, pattern, base);
    chordLabel = `${SCALES[scale].label} ${degree + 1} ${triadQuality(scale, degree)}`;
    idx = 0;
  }

  function start(): void {
    if (!ctxRef || task) return;
    task = ctxRef.transport.addTask({
      bpm, division,
      cb: (t) => {
        if (!engine || seq.length === 0) return;
        const n = pattern === 'random' ? seq[(Math.random() * seq.length) | 0] : seq[idx % seq.length];
        idx++;
        const gate = last?.gate ?? 0.6;
        engine.noteOn(n, t, stepSeconds(bpm, division) * gate, 0.95);
      },
    });
    adapter.setRunning(true);
  }
  function stop(): void { task?.cancel(); task = null; adapter.setRunning(false); }

  return {
    id: 'arp',
    label: 'Arp',
    blurb: 'Diatonic stacks through a pluck or a stab. Spread widens the octave span, aperture is the gate length, height is the level.',
    padMode: 'xy',

    mount(host, ctx) {
      ctxRef = ctx;
      engine = createArpEngine(ctx.bus.ctx, ctx.bus.input, 'pluck');
      rebuild();
      const label = el('b', { class: 'read wide' }, [chordLabel]);
      const refresh = (): void => { rebuild(); label.textContent = chordLabel; };

      host.append(panel('Arpeggiator', 'Every tone is in-scale by construction — the stack is built from scale degrees, not transposed chromatically.', [
        chips<ScaleId>({
          label: 'Scale', value: scale,
          options: SCALE_IDS.map((id) => ({ id, label: SCALES[id].label })),
          onChange: (id) => { scale = id; refresh(); },
        }),
        chips<Quality>({
          label: 'Stack', value: quality,
          options: (Object.keys(QUALITIES) as Quality[]).map((id) => ({ id, label: id.toUpperCase() })),
          onChange: (id) => { quality = id; refresh(); },
        }),
        chips<ArpPattern>({
          label: 'Pattern', value: pattern,
          options: ARP_PATTERNS.map((id) => ({ id, label: id.toUpperCase() })),
          onChange: (id) => { pattern = id; refresh(); },
        }),
        chips<ArpVoiceId>({
          label: 'Voice', value: 'pluck',
          options: ARP_VOICE_IDS.map((id) => ({ id, label: ARP_VOICES[id].label, title: ARP_VOICES[id].note })),
          onChange: (id) => engine?.setVoice(id),
        }),
        el('div', { class: 'field' }, [el('span', { class: 'slabel' }, ['Chord']), label]),
        slider({ label: 'Degree', min: 0, max: 6, step: 1, value: degree, format: (v) => `${v + 1}`, onInput: (v) => { degree = v; refresh(); } }),
        slider({ label: 'Octaves', min: 1, max: 4, step: 1, value: octaves, format: (v) => `${v}`, onInput: (v) => { octaves = v; refresh(); } }),
        slider({ label: 'Root', min: 45, max: 69, step: 1, value: base, format: (v) => `MIDI ${v}`, onInput: (v) => { base = v; refresh(); } }),
        slider({ label: 'Tempo', min: 70, max: 180, step: 1, value: bpm, format: (v) => `${v} bpm`, onInput: (v) => { bpm = v; task?.setBpm(v); } }),
        slider({ label: 'Division', min: 1, max: 8, step: 1, value: division, format: (v) => `1/${v * 4}`, onInput: (v) => { division = v; task?.setDivision(v); } }),
        el('div', { class: 'row' }, [toggle('Run', false, (on) => { on ? start() : stop(); }, 'Start the arpeggio')]),
      ]));
      ctx.setStatus('Arp — degree and octave follow the pad; the sequence itself is pure and tested.');
    },

    update(frame: FeatureFrame, control: ControlState) {
      if (!engine) return;
      const intent = adapter.update(frame, control);
      last = intent;
      engine.setCutoff(intent.cutoffHz);
      engine.setRes(intent.resonance);
      engine.setLevel(intent.level);
      if (intent.octaves !== octaves) { octaves = intent.octaves; rebuild(); }
    },

    selection() {
      if (!last) return null;
      return {
        count: 7,
        active: last.degree,
        label: `${last.degree + 1} ${triadQuality(scale, last.degree)}`,
      };
    },

    readouts() {
      return [
        ['chord', chordLabel || '—'],
        ['notes', String(seq.length)],
        ['gate', last ? last.gate.toFixed(2) : '—'],
        ['cutoff', last ? `${Math.round(last.cutoffHz)} Hz` : '—'],
      ];
    },

    unmount() { stop(); engine?.dispose(); engine = null; adapter.reset(); },
  };
}
